/**
 * **Les comptes d'un guichet** — qui tient ce comptoir, et les trois gestes
 * qui s'y font (V89, 2026-10-01).
 *
 * Aucun chemin de production ne créait un guichetier : on l'écrivait à la main
 * en SQL, sans trace d'audit, et aucune route ne disait qui tient quel
 * comptoir. Ce panneau s'ouvre depuis la table des guichets.
 *
 * - **Créer** un guichetier ou un responsable — SANS code secret. Il choisit
 *   le sien à sa première connexion, après un code SMS sur le numéro de sa
 *   fiche : aucun code ne voyage. En signature unique depuis la V91, sur
 *   décision du porteur.
 * - **Réinitialiser le code** : le code est effacé, les sessions coupées, et le
 *   guichetier en choisit un neuf de la même façon.
 * - **Couper les sessions** : un téléphone perdu, un départ précipité.
 *
 * Chaque geste porte **son propre motif**, saisi : c'est lui que le journal
 * d'audit garde. Le compte rendu vient du serveur, et l'écran ne le paraphrase
 * pas.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { FormShell } from '../shell/FormShell.js';
import type { FormFieldDescriptor } from '../shell/FormShell.js';
import { useApiClient } from '../http/api-context.js';
import { ApiError } from '../http/api-client.js';
import {
  counterRoles,
  createOutletOperator,
  cutOperatorSessions,
  fetchOutletOperators,
  resetOperatorPin,
} from '../http/outlet-operators.js';
import type { OutletOperator } from '../http/outlet-operators.js';
import { changeOperatorStatus } from '../http/outlets.js';
import type { OperatorStatusGesture } from '../http/outlets.js';

/** Le guichet dont on lit les comptes. */
export interface OperatorsOutlet {
  readonly outletId: string;
  readonly outletCode: string;
  readonly businessName: string;
}

/** Le geste en cours : créer, ou agir sur un compte. */
type Geste =
  | { readonly kind: 'create' }
  | { readonly kind: 'reset' | 'cut'; readonly operator: OutletOperator }
  | {
      readonly kind: 'status';
      readonly operator: OutletOperator;
      readonly gesture: OperatorStatusGesture;
    };

/**
 * Les gestes de statut qu'un compte offre, selon son statut (V90). Un compte
 * révoqué n'en offre AUCUN : il ne se rouvre par aucun chemin.
 */
function gestesDeStatut(status: string): readonly OperatorStatusGesture[] {
  switch (status) {
    case 'ACTIF':
      return ['suspend', 'revoke'];
    case 'SUSPENDU':
      return ['reactivate', 'revoke'];
    default:
      return [];
  }
}

/** Le panneau des comptes d'un guichet. */
export function OutletOperatorsPanel({
  outlet,
}: {
  readonly outlet: OperatorsOutlet;
}): ReactNode {
  const { t } = useTranslation();
  const client = useApiClient();

  const [comptes, setComptes] = useState<readonly OutletOperator[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [geste, setGeste] = useState<Geste | null>(null);
  const [erreurGeste, setErreurGeste] = useState<string | null>(null);
  const [avis, setAvis] = useState<string | null>(null);

  const relire = useCallback(async (): Promise<void> => {
    try {
      setComptes(await fetchOutletOperators(client, outlet.outletId));
      setErreur(null);
    } catch (echec) {
      setComptes(null);
      setErreur(echec instanceof ApiError ? echec.messageKey : 'errors.server');
    }
  }, [client, outlet.outletId]);

  useEffect(() => {
    setGeste(null);
    setAvis(null);
    void relire();
  }, [relire]);

  const champsDeCreation: readonly FormFieldDescriptor[] = useMemo(
    () => [
      {
        name: 'fullName',
        labelKey: 'outletNetwork.operators.fields.fullName',
        control: 'text',
        required: true,
      },
      {
        name: 'msisdn',
        labelKey: 'outletNetwork.operators.fields.msisdn',
        control: 'text',
        placeholderKey: 'outletNetwork.operators.fields.msisdnPlaceholder',
        required: true,
      },
      {
        name: 'role',
        labelKey: 'outletNetwork.operators.fields.role',
        control: 'select',
        required: true,
        placeholderKey: 'outletNetwork.operators.fields.rolePlaceholder',
        options: counterRoles.map((role) => ({
          value: role,
          label: t(`outletNetwork.operators.roles.${role}`),
        })),
      },
      {
        name: 'reason',
        labelKey: 'outletNetwork.operators.fields.reason',
        control: 'text',
        required: true,
      },
    ],
    [t],
  );

  const champsDuMotif: readonly FormFieldDescriptor[] = useMemo(
    () => [
      {
        name: 'reason',
        labelKey: 'outletNetwork.operators.fields.reason',
        control: 'text',
        required: true,
      },
    ],
    [],
  );

  const executer = useCallback(
    async (valeurs: Readonly<Record<string, string>>): Promise<void> => {
      if (geste === null) return;
      const motif = valeurs.reason ?? '';
      try {
        const issue =
          geste.kind === 'create'
            ? await createOutletOperator(client, {
                outletCode: outlet.outletCode,
                fullName: valeurs.fullName ?? '',
                msisdn: valeurs.msisdn ?? '',
                role: valeurs.role ?? '',
                reason: motif,
              })
            : geste.kind === 'reset'
              ? await resetOperatorPin(client, geste.operator.operatorId, motif)
              : geste.kind === 'status'
                ? await changeOperatorStatus(
                    client,
                    geste.operator.operatorId,
                    geste.gesture,
                    motif,
                  )
                : await cutOperatorSessions(client, geste.operator.operatorId, motif);
        setErreurGeste(null);
        setGeste(null);
        // Une demande mise en attente — si la matrice remettait une double
        // validation — n'est JAMAIS annoncée comme faite : le guichetier serait
        // envoyé à un comptoir fermé.
        setAvis(
          issue.outcome === 'EXECUTED'
            ? (issue.detail ?? t('outletNetwork.operators.done'))
            : t('outletNetwork.operators.pending', { id: issue.approvalRequestId ?? '' }),
        );
        await relire();
      } catch (echec) {
        setErreurGeste(echec instanceof ApiError ? echec.messageKey : 'errors.server');
      }
    },
    [client, geste, outlet.outletCode, relire, t],
  );

  const ouvrir = (suivant: Geste): void => {
    setGeste(suivant);
    setErreurGeste(null);
    setAvis(null);
  };

  return (
    <section
      aria-label={t('outletNetwork.operators.landmark', { code: outlet.outletCode })}
      className="outlet-network__operators"
      data-testid="outlet-operators"
    >
      <h2 className="outlet-network__section-title">
        {t('outletNetwork.operators.title', {
          code: outlet.outletCode,
          name: outlet.businessName,
        })}
      </h2>
      <p className="outlet-network__meta">{t('outletNetwork.operators.noPinNotice')}</p>

      {avis === null ? null : (
        <p className="outlet-network__avis" role="status">
          {avis}
        </p>
      )}
      {erreur === null ? null : (
        <p className="outlet-network__notice" role="alert">
          {t(erreur)}
        </p>
      )}

      {comptes === null ? (
        erreur === null ? (
          <p className="outlet-network__idle">{t('outletNetwork.loading')}</p>
        ) : null
      ) : comptes.length === 0 ? (
        <p className="outlet-network__idle" data-testid="outlet-operators-empty">
          {t('outletNetwork.operators.empty')}
        </p>
      ) : (
        <div className="outlet-network__table-frame">
          <table className="outlet-network__table" data-testid="outlet-operators-table">
            <thead>
              <tr>
                <th scope="col">{t('outletNetwork.operators.columns.name')}</th>
                <th scope="col">{t('outletNetwork.operators.columns.msisdn')}</th>
                <th scope="col">{t('outletNetwork.operators.columns.role')}</th>
                <th scope="col">{t('outletNetwork.operators.columns.state')}</th>
                <th scope="col">{t('outletNetwork.operators.columns.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {comptes.map((compte) => (
                <tr data-testid={`operator-row-${compte.msisdn}`} key={compte.operatorId}>
                  <td>{compte.fullName}</td>
                  <td data-emphasis="numeric">{compte.msisdn}</td>
                  <td>{t(`outletNetwork.operators.roles.${compte.role}`, compte.role)}</td>
                  {/* L'état se dit par le MOT : actif, en attente de son code,
                      verrouillé — jamais par la seule teinte. */}
                  <td>
                    <span
                      className="outlet-network__state-badge"
                      data-tone={etatDuCompte(compte).tone}
                    >
                      {t(etatDuCompte(compte).key, { status: compte.status })}
                    </span>
                  </td>
                  <td className="outlet-network__operator-actions">
                    <button
                      className="outlet-network__button"
                      onClick={() => {
                        ouvrir({ kind: 'reset', operator: compte });
                      }}
                      type="button"
                    >
                      {t('outletNetwork.operators.resetPin')}
                    </button>
                    <button
                      className="outlet-network__button"
                      onClick={() => {
                        ouvrir({ kind: 'cut', operator: compte });
                      }}
                      type="button"
                    >
                      {t('outletNetwork.operators.cutSessions')}
                    </button>
                    {gestesDeStatut(compte.status).map((gesture) => (
                      <button
                        className="outlet-network__button"
                        data-variant={gesture === 'revoke' ? 'danger' : undefined}
                        key={gesture}
                        onClick={() => {
                          ouvrir({ kind: 'status', operator: compte, gesture });
                        }}
                        type="button"
                      >
                        {t(`outletNetwork.operators.gestures.${gesture}`)}
                      </button>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {geste === null ? (
        <button
          className="outlet-network__button"
          data-variant="primary"
          onClick={() => {
            ouvrir({ kind: 'create' });
          }}
          type="button"
        >
          {t('outletNetwork.operators.create')}
        </button>
      ) : (
        <div className="outlet-network__operator-form" data-testid="outlet-operator-form">
          <h3 className="outlet-network__section-title">
            {geste.kind === 'create'
              ? t('outletNetwork.operators.createTitle')
              : t(
                  geste.kind === 'reset'
                    ? 'outletNetwork.operators.resetTitle'
                    : geste.kind === 'status'
                      ? `outletNetwork.operators.statusTitles.${geste.gesture}`
                      : 'outletNetwork.operators.cutTitle',
                  { name: geste.operator.fullName },
                )}
          </h3>
          {erreurGeste === null ? null : (
            <p className="outlet-network__notice" role="alert">
              {t(erreurGeste)}
            </p>
          )}
          <FormShell
            fields={geste.kind === 'create' ? champsDeCreation : champsDuMotif}
            layout="stacked"
            onSubmit={executer}
            submitLabelKey={
              geste.kind === 'create'
                ? 'outletNetwork.operators.createSubmit'
                : geste.kind === 'reset'
                  ? 'outletNetwork.operators.resetSubmit'
                  : geste.kind === 'status'
                    ? `outletNetwork.operators.statusSubmits.${geste.gesture}`
                    : 'outletNetwork.operators.cutSubmit'
            }
          />
          <button
            className="outlet-network__button"
            onClick={() => {
              setGeste(null);
              setErreurGeste(null);
            }}
            type="button"
          >
            {t('outletNetwork.operators.cancel')}
          </button>
        </div>
      )}
    </section>
  );
}

/** Le mot et la teinte de l'état d'un compte. */
function etatDuCompte(compte: OutletOperator): {
  readonly key: string;
  readonly tone: 'success' | 'warning' | 'danger';
} {
  if (compte.status === 'SUSPENDU') {
    return { key: 'outletNetwork.operators.states.suspended', tone: 'warning' };
  }
  if (compte.status === 'REVOQUE') {
    return { key: 'outletNetwork.operators.states.revoked', tone: 'danger' };
  }
  if (compte.status !== 'ACTIF') {
    return { key: 'outletNetwork.operators.states.inactive', tone: 'danger' };
  }
  if (compte.lockedUntil !== null) {
    return { key: 'outletNetwork.operators.states.locked', tone: 'danger' };
  }
  if (compte.awaitsPin) {
    return { key: 'outletNetwork.operators.states.awaitsPin', tone: 'warning' };
  }
  return { key: 'outletNetwork.operators.states.active', tone: 'success' };
}
